import { initialApiState, ApiCallBase, apiClient } from '@/infrastructure/api'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  SettingsApi,
  PbECommerceSettings,
  SettingsApiUpdateCopiesRequest
} from '@itsrever/rever-api'

const settingsApi = new SettingsApi(undefined, undefined, apiClient)

interface SettingsCall extends ApiCallBase {
  response: PbECommerceSettings
  requestId?: string
}

interface CopiesCall {
  response: Record<string, string>
  loading: boolean
  error: unknown | null
  requestId?: string
}

// Initial State:
interface State {
  settings: SettingsCall
  copies: CopiesCall
  defaultCopies: CopiesCall
}

const initialState: State = {
  settings: initialApiState,
  copies: {
    response: {},
    loading: false,
    error: null
  },
  defaultCopies: {
    response: {},
    loading: false,
    error: null
  }
}

// API Actions:
export const getSettings = createAsyncThunk(
  'settingsApi/getSettings',
  async (ecommerceId: string, { rejectWithValue }) => {
    try {
      const settings = await settingsApi.getECommerceSettings({
        ecommerceId
      })
      return settings.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const getCopies = createAsyncThunk(
  'settingsApi/getCopies',
  async (ecommerceId: string, { rejectWithValue }) => {
    try {
      const copies = await settingsApi.getECommerceTextCopiesV2({
        ecommerceId
      })
      return copies.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
export const getDefaultCopies = createAsyncThunk(
  'settingsApi/getDefaultCopies',
  async (_: void, { rejectWithValue }) => {
    try {
      const copies = await settingsApi.getECommerceTextCopiesV2({
        slug: ''
      })
      return copies.data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export async function updateCopies(args: SettingsApiUpdateCopiesRequest) {
  const updateCopiesResponse = await settingsApi.updateCopies(args)

  return updateCopiesResponse.data
}

// Slice:
const settingsApiSlice = createSlice({
  name: 'settingsApi',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Only the latest request lands: a slower one for the store asked before must not overwrite it.
    builder.addCase(getSettings.pending, (state, action) => {
      state.settings.loading = 'pending'
      state.settings.requestId = action.meta.requestId
    })

    builder.addCase(getSettings.rejected, (state, action) => {
      if (action.meta.requestId !== state.settings.requestId) return
      state.settings.error = action.payload
      state.settings.loading = 'failed'
    })

    builder.addCase(getSettings.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.settings.requestId) return
      state.settings.response = action.payload
      state.settings.loading = 'succeeded'
    })

    builder.addCase(getCopies.pending, (state, action) => {
      state.copies.requestId = action.meta.requestId
      state.copies.loading = true
      state.copies.error = null
      state.copies.response = {}
    })
    builder.addCase(getCopies.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.copies.requestId) return
      if (!action.payload.language_copies) return
      const copiesObj = <Record<string, string>>{}
      action.payload.language_copies.forEach((lang) => {
        if (lang.language !== undefined) {
          lang.copies?.forEach((copy) => {
            const key = lang.language + '-' + copy.tag
            copiesObj[key] = copy.text ?? ''
          })
        }
      })
      state.copies.response = copiesObj
      state.copies.loading = false
    })
    builder.addCase(getCopies.rejected, (state, action) => {
      if (action.meta.requestId !== state.copies.requestId) return
      state.copies.loading = false
      state.copies.error = action.payload
    })

    builder.addCase(getDefaultCopies.pending, (state) => {
      state.defaultCopies.loading = true
      state.defaultCopies.error = null
      state.defaultCopies.response = {}
    })
    builder.addCase(getDefaultCopies.fulfilled, (state, action) => {
      if (!action.payload.language_copies) return
      const copiesObj = <Record<string, string>>{}
      action.payload.language_copies.forEach((lang) => {
        if (lang.language !== undefined) {
          lang.copies?.forEach((copy) => {
            const key = lang.language + '-' + copy.tag
            copiesObj[key] = copy.text ?? ''
          })
        }
      })
      state.defaultCopies.response = copiesObj
      state.defaultCopies.loading = false
    })
    builder.addCase(getDefaultCopies.rejected, (state, action) => {
      state.defaultCopies.loading = false
      state.defaultCopies.error = action.payload
    })
  }
})
export default settingsApiSlice.reducer
